import React, { useMemo, useState } from "react";
import {
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import MonitoringExpandModal, {
	MonitoringChartDataTable,
	MonitoringExpandButton,
} from "../MonitoringExpandModal";
import { fmtDate } from "./trafficUtils";

const COLORS = [
	"#4f46e5",
	"#0ea5e9",
	"#ea580c",
	"#059669",
	"#9333ea",
	"#dc2626",
];

function EngineTrendLineChart({
	chartData,
	series,
	ymin,
	yMax,
	yLabel,
	unit,
	height = 200,
}) {
	return (
		<>
			<p className="monitoring__ai-traffic-chart-y-label">{yLabel}</p>
			<div className="monitoring__ai-traffic-chart-wrap">
				<ResponsiveContainer width="100%" height={height}>
					<LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
						<XAxis
							dataKey="date"
							tickFormatter={fmtDate}
							tick={{ fontSize: 10 }}
							interval="preserveStartEnd"
						/>
						<YAxis domain={[ymin, yMax]} tick={{ fontSize: 10 }} width={36} />
						<Tooltip
							labelFormatter={fmtDate}
							formatter={(value) => [
								value == null ? "—" : `${value}${unit}`,
								"",
							]}
						/>
						{series.map((s, i) => (
							<Line
								key={s.engine}
								type="monotone"
								dataKey={s.engine}
								name={s.label || s.engine}
								stroke={COLORS[i % COLORS.length]}
								strokeWidth={2}
								dot={false}
								connectNulls
							/>
						))}
					</LineChart>
				</ResponsiveContainer>
			</div>
			<div className="monitoring__ai-traffic-chart-legend">
				{series.map((s, i) => (
					<span key={s.engine} className="monitoring__ai-traffic-legend-item">
						<i
							className="monitoring__ai-traffic-legend-swatch"
							style={{ background: COLORS[i % COLORS.length] }}
						/>
						{s.label || s.engine}
					</span>
				))}
			</div>
		</>
	);
}

export default function TrafficEngineTrendChart({
	series,
	ymin = 0,
	ymax,
	title = "Trend",
	yLabel = "Value",
	unit = "",
	expandable = false,
}) {
	const [expanded, setExpanded] = useState(false);

	const dates = useMemo(() => {
		if (!series?.length) return [];
		return Array.from(
			new Set(series.flatMap((s) => (s.points || []).map((p) => p.date)))
		).sort();
	}, [series]);

	const chartData = useMemo(() => {
		if (!series?.length || !dates.length) return [];
		return dates.map((date) => {
			const row = { date };
			series.forEach((s) => {
				const pt = (s.points || []).find((p) => p.date === date);
				row[s.engine] = pt?.value ?? null;
			});
			return row;
		});
	}, [series, dates]);

	const yMax = useMemo(() => {
		const peak = Math.max(
			ymin,
			...series.flatMap((s) => (s.points || []).map((p) => p.value ?? 0))
		);
		return ymax ?? (peak <= 0 ? 10 : Math.ceil((peak * 1.15) / 10) * 10);
	}, [series, ymin, ymax]);

	const tableColumns = useMemo(
		() => [
			{
				key: "date",
				label: "Date",
				render: (row) => fmtDate(row.date),
			},
			...series.map((s) => ({
				key: s.engine,
				label: s.label || s.engine,
				numeric: true,
				render: (row) =>
					row[s.engine] == null ? "—" : `${row[s.engine]}${unit}`,
			})),
		],
		[series, unit]
	);

	if (!series?.length || !dates.length) {
		return (
			<p className="monitoring__ai-traffic-chart-empty mb-0">
				No data yet — runs will populate this chart.
			</p>
		);
	}

	return (
		<div className="monitoring__ai-traffic-engine-chart">
			{expandable ? (
				<div className="monitoring__ai-traffic-section-header">
					<h3 className="monitoring__ai-traffic-section-title mb-0">{title}</h3>
					<MonitoringExpandButton onClick={() => setExpanded(true)} />
				</div>
			) : null}
			<EngineTrendLineChart
				chartData={chartData}
				series={series}
				ymin={ymin}
				yMax={yMax}
				yLabel={yLabel}
				unit={unit}
				height={200}
			/>
			{title && !expandable && (
				<p className="monitoring__ai-traffic-chart-caption mb-0">{title}</p>
			)}

			{expandable && (
				<MonitoringExpandModal
					open={expanded}
					onClose={() => setExpanded(false)}
					title={title}
					chart={
						<div className="monitoring__chart-wrap monitoring__chart-wrap--expand">
							<EngineTrendLineChart
								chartData={chartData}
								series={series}
								ymin={ymin}
								yMax={yMax}
								yLabel={yLabel}
								unit={unit}
								height={360}
							/>
						</div>
					}
					table={
						<MonitoringChartDataTable
							caption={title}
							columns={tableColumns}
							rows={chartData}
						/>
					}
				/>
			)}
		</div>
	);
}
